import {
  Box,
  Checkbox,
  Typography,
  IconButton,
  Menu,
  MenuItem,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { MoreVert, Undo, GetApp, Edit, Delete } from '@material-ui/icons';
import { useState } from 'react';
import { StatusChip } from './appraisalDetailsComponents';
import { StandardDialog, CurrencyField, Button, ConfirmationButton } from '@roc/ui';
import { formatCurrency, paymentStillHoldsMoney, PaymentStatus, AppraisalDetailsV2Store } from '../../../../stores/documentForms/appraisalDetailsV2/appraisalDetailsV2Store';

const RUSH_FEE_AMOUNT = 600;

const usePaymentsStyles = makeStyles(() => ({
  feeNotice: {
    padding: '12px 16px',
    marginBottom: '16px',
    borderRadius: '4px',
    backgroundColor: '#FFF8E1',
    border: '1px solid #FFE082',
  },
  feeNoticeText: {
    fontSize: '14px',
  },
  statsContainer: {
    display: 'flex',
    gap: '24px',
    marginBottom: '32px',
    padding: '24px',
    backgroundColor: '#F9FAFB',
    borderRadius: '8px',
  },
  statItem: {
    flex: 1,
    textAlign: 'center',
  },
  statLabel: {
    fontSize: '14px',
    color: '#6B7280',
    marginBottom: '8px',
    fontWeight: 400,
  },
  statValue: {
    fontSize: '24px',
    fontWeight: 700,
    color: '#111827',
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#111827',
    marginBottom: '16px',
  },
  table: {
    width: '100%',
    borderCollapse: 'separate',
    borderSpacing: 0,
  },
  tableHeader: {
    backgroundColor: '#F9FAFB',
    borderBottom: '1px solid #E5E7EB',
  },
  th: {
    padding: '12px 16px',
    textAlign: 'left',
    fontSize: '12px',
    fontWeight: 600,
    color: '#6B7280',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  tr: {
    borderBottom: '1px solid #E5E7EB',
    '&:hover': {
      backgroundColor: '#F9FAFB',
    },
  },
  td: {
    padding: '16px',
    fontSize: '14px',
    color: '#111827',
  },
  refundedCell: {
    color: '#B45309',
    fontWeight: 500,
  },
  statusCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  statusDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
  },
  pendingDot: {
    backgroundColor: '#EF4444',
  },
  completedDot: {
    backgroundColor: '#10B981',
  },
  statusText: {
    fontSize: '14px',
    fontWeight: 500,
  },
  avatarsContainer: {
    display: 'flex',
    marginLeft: '8px',
  },
  avatar: {
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    border: '2px solid white',
    marginLeft: '-8px',
    '&:first-child': {
      marginLeft: 0,
    },
  },
  editPaymentDialogPaper: {
    width: '760px',
    maxWidth: 'calc(100vw - 48px)',
    '& .MuiDialogTitle-root': {
      padding: '32px 32px 18px',
    },
    '& .MuiDialogContent-root': {
      padding: '0 32px',
    },
    '& .MuiDialogActions-root': {
      padding: '20px 32px 32px',
    },
    '& .MuiDialogTitle-root .MuiIconButton-root': {
      right: '24px',
      top: '24px',
    },
  },
  editPaymentDialogContainer: {
    justifyContent: 'center',
  },
  editPaymentContent: {
    width: '100%',
  },
  allocationList: {
    boxSizing: 'border-box',
    width: '100%',
    border: '1px solid #D1D5DB',
    borderRadius: '8px',
    overflow: 'hidden',
    padding: 0,
    margin: '0 0 18px',
  },
  allocationRow: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) 140px 112px 96px 40px',
    columnGap: '20px',
    alignItems: 'center',
    padding: '16px 22px',
    borderBottom: '1px solid #EEF2F7',
    '&:last-child': {
      borderBottom: 0,
    },
  },
  allocationDeleteButton: {
    minWidth: 'auto',
    padding: '6px',
    color: '#94A3B8',
    '&:hover': {
      color: '#EF4444',
    },
  },
  allocationRushRow: {
    backgroundColor: '#F3F7FC',
  },
  allocationInfo: {
    minWidth: 0,
  },
  allocationProperty: {
    fontSize: '15px',
    fontWeight: 500,
    color: '#111827',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  compactCurrencyField: {
    '& .MuiOutlinedInput-input': {
      padding: '9px 12px',
      textAlign: 'right',
      fontSize: '14px',
      fontWeight: 700,
    },
    '& .MuiInputAdornment-positionStart': {
      marginRight: '4px',
    },
  },
  rushCell: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  rushCheckbox: {
    padding: '4px 8px 4px 0',
    color: '#94A3B8',
    '&.Mui-checked': {
      color: '#1F3D64',
    },
  },
  rushLabel: {
    fontSize: '14px',
    fontWeight: 700,
    color: '#1F2937',
    lineHeight: 1.2,
  },
  rushFee: {
    display: 'block',
    fontSize: '11px',
    fontWeight: 400,
    color: '#6B7280',
  },
  allocationRowTotal: {
    textAlign: 'right',
    fontSize: '15px',
    fontWeight: 700,
    color: '#111827',
  },
  totalsBox: {
    boxSizing: 'border-box',
    width: '100%',
    backgroundColor: '#F9FAFB',
    borderRadius: '8px',
    padding: '16px 24px',
    marginBottom: '14px',
  },
  totalLine: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '6px 0',
    fontSize: '15px',
    color: '#374151',
  },
  grandTotalLine: {
    fontWeight: 700,
    fontSize: '16px',
    color: '#111827',
  },
  summaryDivider: {
    borderTop: '1px solid #E5E7EB',
    marginTop: '6px',
    paddingTop: '12px',
  },
}));

type PaymentAllocation = {
  valuationPaymentId?: number | string;
  clientValuationReferenceId: number;
  propertyId?: number;
  propertyAppraisalId?: number;
  propertyLabel: string;
  propertyAddress?: string;
  address?: string;
  property?: {
    propertyAddress?: string;
    address?: string;
  };
  orderLabel: string;
  appraisalType?: string | null;
  amount: number | string;
  isRush: boolean;
  submitRushedAppraisal?: boolean;
  rushFeeAmount: number;
  rushFeePaid?: boolean;
  rushedAppraisalFeePaid?: boolean;
};

type Payment = {
  referenceId: string;
  status: PaymentStatus;
  purpose: string;
  payableTo?: string | null;
  paidOn: string | null;
  amount: string;
  refundedAmount?: string | null;
  amountValue?: number | null;
  refundableValue?: number | null;
  avatars?: string[];
  allocations?: PaymentAllocation[];
  // Payment left on a valuation that was reset: listed for the record, never edited or refunded.
  fromDeletedValuation?: boolean;
};

type Props = {
  totalPaid: string;
  pendingPayments: string;
  lastPayment: string;
  payments: Payment[];
  store: AppraisalDetailsV2Store;
  onDownloadReceipt?: (referenceId: string) => void;
  onDownloadPaymentAuthorization?: (referenceId: string) => void;
};

export const AppraisalPaymentsTab = ({
  totalPaid = '$1,100',
  pendingPayments = '$1,000',
  lastPayment = '02/12/2026 3:24 pm',
  payments = [],
  store,
  onDownloadReceipt,
  onDownloadPaymentAuthorization,
}: Props) => {
  const classes = usePaymentsStyles();
  const [actionAnchorEl, setActionAnchorEl] = useState<null | HTMLElement>(
    null
  );
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(
    null
  );

  const [refundAmount, setRefundAmount] = useState<number | string | null>(null);
  const [allocationDrafts, setAllocationDrafts] = useState<PaymentAllocation[]>([]);

  const [openRefundModal, setOpenRefundModal] = useState(false);
  const refundable = selectedPayment?.refundableValue ?? null;
  const exceedsRefundable =
    refundable != null && Number(refundAmount ?? 0) > refundable;
  const [openEditPaymentModal, setOpenEditPaymentModal] = useState(false);
  const [isSavingPayment, setIsSavingPayment] = useState(false);

  const {globalStore} = store;

  const parseAmount = (value: number | string | null | undefined): number => {
    if (typeof value === 'number') return Number.isNaN(value) ? 0 : value;
    if (!value) return 0;

    const parsed = Number(value.replace(/[^0-9.-]+/g, ''));
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  const formatAmount = (value: number): string =>
    `$${value.toLocaleString(undefined, {
      minimumFractionDigits: value % 1 === 0 ? 0 : 2,
      maximumFractionDigits: 2,
    })}`;

  const splitAmountEvenly = (amount: number, count: number): number[] => {
    if (count <= 0) return [];

    const cents = Math.round(amount * 100);
    const base = Math.floor(cents / count);
    const remainder = cents - base * count;

    return Array.from({ length: count }, (_, index) =>
      (base + (index < remainder ? 1 : 0)) / 100
    );
  };

  const resolvePropertyLabel = (propertyId: number): string => {
    const property = store.properties.find(p => p.propertyId === propertyId);
    return property?.propertyAddress || property?.address || `Property #${propertyId}`;
  };

  const resolveAllocationPropertyLabel = (allocation: PaymentAllocation): string => {
    const suppliedLabel = String(allocation.propertyLabel ?? '').trim();
    const labelLooksLikeId = /^\d+$/.test(suppliedLabel);
    const relatedValuation = store.valuations.find(
      valuation =>
        valuation.clientValuationReferenceId === allocation.clientValuationReferenceId ||
        valuation.valuationId === allocation.propertyAppraisalId ||
        valuation.propertyId === allocation.propertyAppraisalId
    );
    const relatedPropertyAddress = relatedValuation
      ? resolvePropertyLabel(relatedValuation.propertyId)
      : '';

    return (
      allocation.propertyAddress ||
      allocation.address ||
      allocation.property?.propertyAddress ||
      allocation.property?.address ||
      relatedPropertyAddress ||
      (!labelLooksLikeId ? suppliedLabel : '') ||
      'Valuation Order'
    );
  };

  const buildAllocationDrafts = (payment: Payment | null): PaymentAllocation[] => {
    if (!payment) return [];

    if (Array.isArray(payment.allocations) && payment.allocations.length > 0) {
      const splitAmounts = splitAmountEvenly(
        parseAmount(payment.amount),
        payment.allocations.length
      );

      return payment.allocations.map((allocation, index) => ({
        valuationPaymentId: allocation.valuationPaymentId,
        clientValuationReferenceId: allocation.clientValuationReferenceId,
        propertyId: allocation.propertyId,
        propertyAppraisalId: allocation.propertyAppraisalId,
        propertyLabel: resolveAllocationPropertyLabel(allocation),
        propertyAddress: allocation.propertyAddress,
        address: allocation.address,
        orderLabel: allocation.orderLabel || `Order ${allocation.clientValuationReferenceId}`,
        appraisalType: allocation.appraisalType,
        amount: allocation.amount == null
          ? splitAmounts[index] ?? 0
          : parseAmount(allocation.amount),
        isRush: Boolean(allocation.isRush || allocation.submitRushedAppraisal),
        rushFeeAmount: allocation.rushFeeAmount ?? RUSH_FEE_AMOUNT,
        rushFeePaid: Boolean(allocation.rushFeePaid || allocation.rushedAppraisalFeePaid),
      }));
    }

    if (store.valuations.length === 0) {
      return [
        {
          valuationPaymentId: payment.referenceId,
          clientValuationReferenceId: Number(payment.referenceId),
          propertyLabel: 'Valuation Order',
          orderLabel: `Payment #${payment.referenceId}`,
          appraisalType: payment.purpose,
          amount: parseAmount(payment.amount),
          isRush: false,
          rushFeeAmount: RUSH_FEE_AMOUNT,
        },
      ];
    }

    const splitAmounts = splitAmountEvenly(
      parseAmount(payment.amount),
      store.valuations.length
    );

    return store.valuations.map((valuation, index) => {
      const orderLabel =
        valuation.orderNumber ||
        valuation.draftNumber ||
        `Order ${valuation.clientValuationReferenceId}`;

      return {
        clientValuationReferenceId: valuation.clientValuationReferenceId,
        propertyId: valuation.propertyId,
        propertyLabel: resolvePropertyLabel(valuation.propertyId),
        orderLabel,
        appraisalType: valuation.valuationType,
        amount: splitAmounts[index] ?? 0,
        isRush: false,
        rushFeeAmount: RUSH_FEE_AMOUNT,
      };
    });
  };

  const baseAmountTotal = allocationDrafts.reduce(
    (total, allocation) => total + parseAmount(allocation.amount),
    0
  );

  const rushFeeTotal = allocationDrafts.reduce(
    (total, allocation) =>
      total + (allocation.isRush && !allocation.rushFeePaid
        ? allocation.rushFeeAmount ?? RUSH_FEE_AMOUNT
        : 0),
    0
  );

  const rushCount = allocationDrafts.filter(
    allocation => allocation.isRush && !allocation.rushFeePaid
  ).length;

  const paymentTotal = baseAmountTotal + rushFeeTotal;

  const getAllocationTotal = (allocation: PaymentAllocation): number =>
    parseAmount(allocation.amount) +
    (allocation.isRush && !allocation.rushFeePaid
      ? allocation.rushFeeAmount ?? RUSH_FEE_AMOUNT
      : 0);

  const handleActionClick = (
    event: React.MouseEvent<HTMLElement>,
    payment: Payment
  ) => {
    setActionAnchorEl(event.currentTarget);
    setSelectedPayment(payment);
    setRefundAmount(payment.amount);
  };

  const handleActionClose = () => {
    setActionAnchorEl(null);
    setSelectedPayment(null);
  };

  const handleRefund = async () => {
    await store.refundPayment(selectedPayment?.referenceId, refundAmount || 0);
    handleActionClose();
    setOpenRefundModal(false);
    setRefundAmount(null);
  };

  const handleDownloadReceipt = () => {
    if (selectedPayment?.referenceId) {
      onDownloadReceipt?.(selectedPayment.referenceId);
    }
    handleActionClose();
  };

  const handleDownloadPaymentAuthorization = () => {
    onDownloadPaymentAuthorization?.(selectedPayment?.referenceId);
    handleActionClose();
  };

  const handleOpenEditPaymentModal = () => {
    setAllocationDrafts(buildAllocationDrafts(selectedPayment));
    setActionAnchorEl(null);
    setOpenEditPaymentModal(true);
  };

  const handleAllocationAmountChange = (index: number, value: number | string) => {
    setAllocationDrafts(prev =>
      prev.map((allocation, allocationIndex) =>
        allocationIndex === index ? { ...allocation, amount: value } : allocation
      )
    );
  };

  const handleAllocationRushChange = (index: number, checked: boolean) => {
    setAllocationDrafts(prev =>
      prev.map((allocation, allocationIndex) =>
        allocationIndex === index
          ? { ...allocation, isRush: checked }
          : allocation
      )
    );
  };

  const handleRemoveAllocation = (index: number) => {
    setAllocationDrafts(prev => prev.filter((_, allocationIndex) => allocationIndex !== index));
  };

  const buildPaymentAllocationPayload = () =>
    allocationDrafts.map(allocation => ({
      valuationPaymentId: allocation.valuationPaymentId,
      clientValuationReferenceId: allocation.clientValuationReferenceId,
      propertyId: allocation.propertyId,
      propertyAddress: allocation.propertyAddress || allocation.address || allocation.propertyLabel,
      amount: parseAmount(allocation.amount),
      isRush: Boolean(allocation.isRush),
    }));

  const handleEditPaymentAmount = async () => {
    if (isSavingPayment) return;

    setIsSavingPayment(true);
    try {
      await store.editPayment({
        referenceId: selectedPayment?.referenceId,
        status: selectedPayment?.status,
        purpose: selectedPayment?.purpose,
        paidOn: selectedPayment?.paidOn,
        amount: paymentTotal,
        allocations: buildPaymentAllocationPayload(),
      });
      handleActionClose();
      setOpenEditPaymentModal(false);
      setAllocationDrafts([]);
    } finally {
      setIsSavingPayment(false);
    }
  };

  return (
    <Box>
      {store.feeReconciliation?.outcome === 'REFUND' &&
        store.feeReconciliation?.difference ? (
          <Box className={classes.feeNotice} data-testid="valuation-fee-surplus">
            <Typography className={classes.feeNoticeText}>
              This loan has received{' '}
              <strong>{formatCurrency(store.feeReconciliation.difference)}</strong>{' '}
              extra in payments over its current valuation.
            </Typography>
          </Box>
        ) : null}
      {/* Stats Section */}
      <Box className={classes.statsContainer}>
        <Box className={classes.statItem}>
          <Typography className={classes.statLabel}>Total Paid</Typography>
          <Typography className={classes.statValue}>{totalPaid}</Typography>
        </Box>
        <Box className={classes.statItem}>
          <Typography className={classes.statLabel}>
            Pending Payments
          </Typography>
          <Typography className={classes.statValue}>
            {pendingPayments}
          </Typography>
        </Box>
        <Box className={classes.statItem}>
          <Typography className={classes.statLabel}>Last Payment</Typography>
          <Typography className={classes.statValue}>{lastPayment}</Typography>
        </Box>
      </Box>

      {/* Payments Table Section */}
      <Typography className={classes.sectionTitle}>Payments</Typography>
      <table className={classes.table}>
        <thead className={classes.tableHeader}>
          <tr>
            <th className={classes.th}>Reference ID</th>
            <th className={classes.th}>Status</th>
            <th className={classes.th}>Purpose</th>
            <th className={classes.th}>Payable To</th>
            <th className={classes.th}>Paid On</th>
            <th className={classes.th}>Amount</th>
            <th className={classes.th}>Refunded</th>
            <th className={classes.th}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {payments.map(payment => (
            <tr key={payment.referenceId} className={classes.tr}>
              <td className={classes.td}>{payment.referenceId}</td>
              <td className={classes.td}>
                <Box className={classes.statusCell}>
                  <StatusChip status={payment.status} />
                </Box>
              </td>
              <td className={classes.td}>{payment.purpose}</td>
              <td className={classes.td}>{payment.payableTo || '-'}</td>
              <td className={classes.td}>{payment.paidOn || '-'}</td>
              <td className={classes.td}>{payment.amount}</td>
              <td className={classes.td}>
                {payment.refundedAmount ? (
                  <span className={classes.refundedCell}>{payment.refundedAmount}</span>
                ) : (
                  '-'
                )}
              </td>
              <td className={classes.td}>
                <IconButton
                  size="small"
                  onClick={e => handleActionClick(e, payment)}
                >
                  <MoreVert />
                </IconButton>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <Menu
        anchorEl={actionAnchorEl}
        open={Boolean(actionAnchorEl)}
        onClose={handleActionClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {paymentStillHoldsMoney(selectedPayment?.status) &&
        <>
        {globalStore.isInternalPortal && !selectedPayment?.fromDeletedValuation && <MenuItem onClick={() => setOpenRefundModal(true)}>
          <Undo fontSize="small" style={{ marginRight: '8px' }} />
          Refund Valuation Cost
        </MenuItem>}
        <MenuItem onClick={handleDownloadReceipt}>
          <GetApp fontSize="small" style={{ marginRight: '8px' }} />
          Download Receipt
        </MenuItem>
        <MenuItem onClick={handleDownloadPaymentAuthorization}>
          <GetApp fontSize="small" style={{ marginRight: '8px' }} />
          Download Payment Authorization
        </MenuItem>
        </>}
        {selectedPayment?.status === 'Pending' && globalStore.isInternalPortal && !selectedPayment?.fromDeletedValuation && <MenuItem onClick={handleOpenEditPaymentModal}>
          <Edit fontSize="small" style={{ marginRight: '8px' }} />
          Edit Payment Amount
        </MenuItem>}
      </Menu>
      <StandardDialog
        open={openRefundModal}
        title="Valuation Refund"
        maxWidth="xs"
        handleClose={() => setOpenRefundModal(false)}
        dialogContent={
        <Box width={'100%'}>
          <CurrencyField
            testId={'refundAmount'}
            variant={'outlined'}
            label={'Please enter the refund amount of your order'}
            value={refundAmount ?? ''}
            onChange={(name, value) => {
              setRefundAmount(Number(value));
            }}
            error={exceedsRefundable}
            errorText={
              selectedPayment?.refundableValue != null
                ? `${formatCurrency(selectedPayment.refundableValue)} of this payment is still held`
                : undefined
            }
            fullWidth
          />
        </Box>}
        dialogActions={
        <>
          <Button
            testId="cancel-refund"
            type="button"
            color="primary"
            variant="outlined"
            style={{ marginRight: '8px' }}
            onClick={() => setOpenRefundModal(false)}
          >
            Cancel
          </Button>
          <Button
            testId="submit-refund"
            type="button"
            color="primary"
            variant="contained"
            disabled={exceedsRefundable}
            onClick={handleRefund}
          >
            Submit
          </Button>
        </>}
        removeContentBox
      />
      <StandardDialog
        open={openEditPaymentModal}
        title="Payment Amount"
        subTitle="Enter the amount for each order. Rush adds $600 per order."
        maxWidth="md"
        classes={{
          container: classes.editPaymentDialogContainer,
          paper: classes.editPaymentDialogPaper,
        }}
        handleClose={() => {
          setOpenEditPaymentModal(false);
          setAllocationDrafts([]);
        }}
        dialogContent={
        <Box className={classes.editPaymentContent}>
          <Box className={classes.allocationList}>
            {allocationDrafts.map((allocation, index) => (
              <Box
                key={`${allocation.clientValuationReferenceId}-${index}`}
                className={`${classes.allocationRow} ${allocation.isRush ? classes.allocationRushRow : ''}`}
              >
                <Box className={classes.allocationInfo}>
                  <Typography className={classes.allocationProperty}>
                    {allocation.propertyLabel}
                  </Typography>
                </Box>
                <CurrencyField
                  testId={`paymentAmount-${allocation.clientValuationReferenceId}`}
                  variant={'outlined'}
                  value={allocation.amount ?? ''}
                  onChange={(name, value) => handleAllocationAmountChange(index, value)}
                  inputClassname={classes.compactCurrencyField}
                  fullWidth
                />
                <Box className={classes.rushCell}>
                  <Checkbox
                    className={classes.rushCheckbox}
                    color="primary"
                    size="small"
                    checked={Boolean(allocation.isRush)}
                    disabled={Boolean(allocation.rushFeePaid)}
                    onChange={event =>
                      handleAllocationRushChange(index, event.target.checked)
                    }
                  />
                  <Typography className={classes.rushLabel}>
                    Rush
                    <span className={classes.rushFee}>
                      {allocation.rushFeePaid
                        ? 'Fee paid'
                        : `+${formatAmount(allocation.rushFeeAmount ?? RUSH_FEE_AMOUNT)}`}
                    </span>
                  </Typography>
                </Box>
                <Typography className={classes.allocationRowTotal}>
                  {formatAmount(getAllocationTotal(allocation))}
                </Typography>
                <ConfirmationButton
                  testId={`removeAllocation-${allocation.clientValuationReferenceId}-${index}`}
                  className={classes.allocationDeleteButton}
                  disabled={allocationDrafts.length <= 1}
                  confirmDialogProps={{
                    title: 'Remove order',
                    body: `Remove ${allocation.propertyLabel} — ${allocation.orderLabel} from this payment?`,
                  }}
                  onClick={() => handleRemoveAllocation(index)}
                >
                  <Delete fontSize="small" />
                </ConfirmationButton>
              </Box>
            ))}
          </Box>
          <Box className={classes.totalsBox}>
            <Box className={classes.totalLine}>
              <span>Subtotal ({allocationDrafts.length} orders)</span>
              <span>{formatAmount(baseAmountTotal)}</span>
            </Box>
            <Box className={classes.totalLine}>
              <span>Rush fees ({rushCount} x {formatAmount(RUSH_FEE_AMOUNT)})</span>
              <span>{formatAmount(rushFeeTotal)}</span>
            </Box>
            <Box className={`${classes.totalLine} ${classes.grandTotalLine} ${classes.summaryDivider}`}>
              <span>Total Payment</span>
              <strong>{formatAmount(paymentTotal)}</strong>
            </Box>
          </Box>
        </Box>}
        dialogActions={
        <>
          <Button
            testId="cancel-payment-amount"
            type="button"
            color="primary"
            variant="outlined"
            style={{ marginRight: '8px' }}
            disabled={isSavingPayment}
            onClick={() => {
              setOpenEditPaymentModal(false);
              setAllocationDrafts([]);
            }}
          >
            Cancel
          </Button>
          <Button
            testId="submit-payment-amount"
            type="button"
            color="primary"
            variant="contained"
            disabled={isSavingPayment || paymentTotal <= 0 || allocationDrafts.length === 0}
            onClick={handleEditPaymentAmount}
          >
            {isSavingPayment ? 'Saving...' : 'Submit'}
          </Button>
        </>}
        removeContentBox
      />
    </Box>
  );
};
